import styled from 'styled-components';

// Components
import { Typography } from '../Typography';
import { Definition } from '../Definition';
import { SpecialTag } from '../SpecialTag';

export const SpecialTagStyled = styled(SpecialTag)`
  cursor: pointer;
`;

/**
 * El resumen SIEMPRE apila en vertical y ocupa todo el ancho disponible.
 *
 * Antes esto vivía dentro de `@media (max-width: ${hasBudgetItems ? 243 : 120}rem)`,
 * con dos problemas encadenados:
 *
 *  1. **Trampa de unidades**: en una media query `rem` se resuelve contra el
 *     tamaño de fuente INICIAL (16 px), NO contra el `:root { font-size: 62.5% }`
 *     de `global-styles.ts`. El comentario original ("2430px = 243rem * 0.625")
 *     daba por hecho lo contrario, así que `120rem` no eran 1200 px sino
 *     **1920 px** — justo el ancho a partir del cual el usuario reportaba que
 *     "se rompe".
 *  2. **Layout acoplado al ROL**: `hasBudgetItems` sale de
 *     `roleCanViewBudgets(currentRole)`, o sea que el umbral cambiaba según
 *     quién mirase. Por encima de 1920 px un Super Admin (ve partidas → umbral
 *     3888 px) seguía viendo el resumen apilado a todo el ancho, mientras que
 *     Tablet o Capataz (ciegos a partidas → umbral 1920 px) caían en un layout
 *     a dos columnas que exprimía fotos, materiales y operarios dentro de una
 *     barra lateral del ~25 % y recortaba el mapa.
 *
 * Al apilar siempre, todos los roles ven el mismo resumen a ancho completo en
 * cualquier monitor, que es el comportamiento de la versión publicada.
 */
export const GeneralContainer = styled.div`
  width: 100%;
  display: flex;
  flex-direction: column;
  /* Mismo gap que el inter-card dentro de cada columna (2rem) para que en
     modo apilado no haya un salto visual entre la última card de la columna
     izquierda y la primera de la derecha. */
  gap: 2rem;
`;

export const FirstGeneralColumn = styled.div`
  display: flex;
  gap: 2rem;
  flex-wrap: wrap;
  /* Bug #6: First (BasicInfo+Map+Description) ocupa más para que el mapa
     dentro alcance ~50% del ancho total. Antes 1.2/0.8 → mapa al 30%. */
  flex: 1.5;

  /* La descripción (último hijo) pide media fila: con BasicInfo + mapa ya
     ocupando la primera, envuelve sola a la segunda y ahí crece al 100%. */
  & > div:last-child {
    flex-basis: calc(50% - 1rem);
  }
  @media (max-width: ${({ theme }) =>
    theme.breakPoints.landscapeTablet * theme.percentBaseResponsive}rem) {
    & > div:last-child {
      flex-basis: 100%;
    }
  }
`;

export const SecondGeneralColumn = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2rem;
  /* Bug #6: columna de tabs/botones más estrecha para liberar ancho al mapa */
  flex: 0.5;
`;

/**
 * Pergamino: contenedor que apila verticalmente las secciones del resumen
 * (operarios, materiales, maquinaria, animales, partidas) en lugar de
 * esconderlas tras pestañas. El scroll lo aporta la página; aquí solo
 * apilamos con separación entre tarjetas.
 */
export const PergaminoList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2rem;
  width: 100%;
`;

export const BasicInfo = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2rem;
  /* Bug #6: BasicInfo cede peso al MapContainer dentro de FirstGeneralColumn */
  flex: 1;
`;

export const Container = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing.s};
  flex: 1;
  padding: ${({ theme }) => theme.spacing.xl};
  background-color: ${({ theme }) =>
    theme.colors.backgroundLila}; /* CAMBIO: Color lila según Figma */
  border-radius: 0.8rem;
`;

export const MapContainer = styled.div`
  min-height: 40rem;
  min-width: 40rem;
  /* Bug #6: el mapa gana peso frente a BasicInfo dentro de la misma fila. */
  flex: 1.5;
  /* Después del atajo flex a propósito: ese atajo pone basis en 0 y aquí lo
     fijamos a media fila para que mapa y BasicInfo compartan la primera línea. */
  flex-basis: calc(50% - 1rem);
  @media (max-width: ${({ theme }) =>
    theme.breakPoints.landscapeTablet * theme.percentBaseResponsive}rem) {
    flex-basis: 100%;
    min-height: 50rem;
  }
`;

export const TabContainer = styled(Container)`
  height: 100%;
  gap: ${({ theme }) => theme.spacing.xl};
  flex-basis: calc(50% - 1rem);
`;

export const Title = styled(Typography)`
  @media (max-width: ${({ theme }) =>
    theme.breakPoints.landscapeTablet * theme.percentBaseResponsive}rem) {
    font-size: ${({ theme }) => theme.fontSize.M};
  }
`;

export const HeaderContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: ${({ theme }) => theme.spacing.m};
  flex-wrap: wrap;
`;

export const ContentContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing.xl};
`;

export const ColumnsContainer = styled.div`
  display: flex;
  flex: 1;
  gap: ${({ theme }) => theme.spacing.xl} ${({ theme }) => theme.spacing.xxxl};
  @media (max-width: ${({ theme }) =>
    theme.breakPoints.landscapeTablet * theme.percentBaseResponsive}rem) {
    flex-wrap: wrap;
    gap: ${({ theme }) => theme.spacing.m};
  }
  @media (max-width: ${({ theme }) =>
    theme.breakPoints.portraitTablet * theme.percentBaseResponsive}rem) {
    gap: ${({ theme }) => theme.spacing.m};
  }
`;

export const TitleTableContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing.s};
`;

export const StyledColumnsContainer = styled(ColumnsContainer)`
  gap: ${({ theme }) => theme.spacing.xl};
  flex-wrap: nowrap;
  @media (max-width: ${({ theme }) =>
    theme.breakPoints.landscapeTablet * theme.percentBaseResponsive}rem) {
    flex-wrap: wrap;
    gap: ${({ theme }) => theme.spacing.m};
  }
  @media (max-width: ${({ theme }) =>
    theme.breakPoints.portraitTablet * theme.percentBaseResponsive}rem) {
    flex-wrap: wrap;
    gap: ${({ theme }) => theme.spacing.m};
  }
`;

export const BreakdownRow = styled(ColumnsContainer)`
  gap: ${({ theme }) => theme.spacing.l};
  flex-wrap: wrap;

  @media (max-width: ${({ theme }) =>
    theme.breakPoints.landscapeTablet * theme.percentBaseResponsive}rem) {
    gap: ${({ theme }) => theme.spacing.m};
  }
`;

export const Column = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing.xxs};

  @media (max-width: ${({ theme }) =>
    theme.breakPoints.portraitTablet * theme.percentBaseResponsive}rem) {
    flex-basis: 100%;
    width: 100%;
  }
`;

export const ContainerButtons = styled.form`
  display: flex;
  align-self: end;
  gap: ${({ theme }) => theme.spacing.s};
`;

export const StyledDefinition = styled(Definition)`
  & > dt {
    font-weight: ${({ theme }) => theme.fontWeight.semiBold};
  }

  @media (max-width: ${({ theme }) =>
    theme.breakPoints.portraitTablet * theme.percentBaseResponsive}rem) {
    & > dt,
    & > dd {
      font-size: ${({ theme }) => theme.fontSize.XS};
    }
  }
`;

// Fila de <Definition> cuyo valor es un NOMBRE de persona (creador / operario
// que registra). Los nombres no tienen tope de longitud y esta columna del
// resumen se queda en ~231 px a pantalla completa —el mapa ocupa el resto de la
// fila—, así que «Administrador Sistema» se partía por la mitad y quedaba
// apelmazado. Con wrap el valor baja ENTERO a la línea de abajo en lugar de
// trocear el nombre. A 1023 y 600 px la fila mide 265/456 px, cabe de una pieza
// y esto no cambia nada. Medido, no supuesto.
export const PersonDefinition = styled(Definition)`
  flex-wrap: wrap;
`;
